import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DoorVisitReportDownloadButton } from '../DoorVisitReportDownloadButton';
import { DoorVisitTableFooter } from '../DoorVisitTableFooter';
import { createDoorVisitReportApi, type DoorVisitReportApi, type DoorVisitReportFetchImpl, type DoorVisitReportRun } from '../doorVisitReportApi';
import {
  getDoorVisitReportProgressText,
  getDoorVisitReportStatusLabel,
  getDoorVisitReportTerminalAnnouncement,
} from '../doorVisitReportStatus';
import { DoorVisitReportStatus } from '../../graphql/__generated__/graphql';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { PageControlsBar } from '../PageControlsBar';
import { PageHeader } from '../PageHeader';
import { type DoorVisitReportRequestIdGenerator, generateDoorVisitReportRequestId } from '../DoorVisitReportControls';
import { useDoorVisitReportPolling } from '../useDoorVisitReportPolling';
import { useDoorVisitReportRuns } from './useDoorVisitReportRuns';

export type DoorVisitReportRunsPageProps = {
  fetchImpl?: DoorVisitReportFetchImpl;
  api?: DoorVisitReportApi;
  requestIdGenerator?: DoorVisitReportRequestIdGenerator;
};

const formatTimestamp = (value: unknown): string => {
  const date = value instanceof Date ? value : new Date(typeof value === 'string' ? value : '');
  return Number.isNaN(date.valueOf()) ? '—' : date.toLocaleString();
};

type ReportRunRowProps = {
  report: DoorVisitReportRun;
  api: DoorVisitReportApi;
  recoveryKey: number;
  requestIdGenerator: DoorVisitReportRequestIdGenerator;
  onReportChange: (report: DoorVisitReportRun) => void;
  onReportDeleted: (reportId: string) => void;
  onAutomaticError: () => void;
  onTerminal: (announcement: string, report: DoorVisitReportRun) => void;
};

const ReportRunRow = ({
  report,
  api,
  recoveryKey,
  requestIdGenerator,
  onReportChange,
  onReportDeleted,
  onAutomaticError,
  onTerminal,
}: ReportRunRowProps) => {
  const [retrying, setRetrying] = useState(false);
  const [retryRequestId, setRetryRequestId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const isTerminal = report.status === DoorVisitReportStatus.Completed || report.status === DoorVisitReportStatus.Failed;

  useDoorVisitReportPolling({
    report,
    api,
    onReportChange,
    onAutomaticError,
    onTerminal,
    recoveryKey,
  });

  const retryReport = async (): Promise<void> => {
    if (retrying) return;
    const requestId = retryRequestId ?? requestIdGenerator();
    setRetryRequestId(requestId);
    setRetrying(true);
    setRetryError(null);

    try {
      const retriedReport = await api.retryReport(report.id, requestId);
      onReportChange(retriedReport);
      const terminalAnnouncement = getDoorVisitReportTerminalAnnouncement(retriedReport);
      if (terminalAnnouncement !== null) onTerminal(terminalAnnouncement, retriedReport);
      setRetryRequestId(null);
    } catch {
      setRetryError('Could not retry the report. Try again.');
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (isTerminal) return;
    setDeleteDialogOpen(false);
    setDeleteError(null);
  }, [isTerminal]);

  const closeDeleteDialog = (): void => {
    if (deleting) return;
    setDeleteError(null);
    setDeleteDialogOpen(false);
  };

  const deleteReport = async (): Promise<void> => {
    if (deleting || !isTerminal) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const deleted = await api.deleteReport(report.id);
      if (!deleted) throw new Error('Report deletion was not confirmed');
      onReportDeleted(report.id);
      setDeleteDialogOpen(false);
    } catch {
      setDeleteError('Could not delete the report. Try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <TableRow>
      <TableCell>{formatTimestamp(report.createdAt)}</TableCell>
      <TableCell>{report.requesterName}</TableCell>
      <TableCell sx={{ maxWidth: 320, overflowWrap: 'anywhere' }}>{report.filterSummary}</TableCell>
      <TableCell>{getDoorVisitReportStatusLabel(report)}</TableCell>
      <TableCell>{getDoorVisitReportProgressText(report)}</TableCell>
      <TableCell>{formatTimestamp(report.completedAt)}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {report.status === DoorVisitReportStatus.Completed && (
            <DoorVisitReportDownloadButton
              report={report}
              api={api}
              accessibleName={`Download report ${report.id}`}
              compact
            />
          )}
          {report.status === DoorVisitReportStatus.Failed && (
            <Tooltip title={`Retry report ${report.id}`}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => void retryReport()}
                  disabled={retrying}
                  aria-busy={retrying}
                  aria-label={`Retry report ${report.id}`}
                >
                  {retrying ? <CircularProgress size={16} aria-hidden /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {isTerminal && (
            <Tooltip title={`Delete report ${report.id}`}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteDialogOpen(true);
                  }}
                  disabled={deleting}
                  aria-busy={deleting}
                  aria-label={`Delete report ${report.id}`}
                >
                  <DeleteOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        {retryError !== null && <Typography role="alert" variant="caption">{retryError}</Typography>}
        <Dialog
          open={deleteDialogOpen}
          onClose={closeDeleteDialog}
          aria-labelledby={`delete-report-${report.id}-title`}
          aria-describedby={`delete-report-${report.id}-description`}
          aria-busy={deleting}
        >
          <DialogTitle id={`delete-report-${report.id}-title`}>Delete report run?</DialogTitle>
          <DialogContent>
            <DialogContentText id={`delete-report-${report.id}-description`}>
              This permanently removes this report from history. This cannot be undone.
            </DialogContentText>
            {deleteError !== null && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
            <Button color="error" variant="contained" onClick={() => void deleteReport()} disabled={deleting}>
              {deleting ? 'Deleting…' : deleteError === null ? 'Delete report' : 'Retry deletion'}
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    </TableRow>
  );
};

export const DoorVisitReportRunsPage = ({
  fetchImpl,
  api: apiOverride,
  requestIdGenerator = generateDoorVisitReportRequestId,
}: DoorVisitReportRunsPageProps) => {
  const api = useMemo(() => apiOverride ?? createDoorVisitReportApi({ fetchImpl }), [apiOverride, fetchImpl]);
  const reportRuns = useDoorVisitReportRuns({ api });
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [pollingRecoveryKey, setPollingRecoveryKey] = useState(0);
  const [terminalAnnouncement, setTerminalAnnouncement] = useState('');
  const announcedTerminalsRef = useRef(new Set<string>());

  useEffect(() => {
    if (reportRuns.successfulManualRefreshVersion === 0) return;
    setPollingError(null);
    setPollingRecoveryKey(key => key + 1);
  }, [reportRuns.successfulManualRefreshVersion]);

  const handleTerminal = useCallback((announcement: string, report: DoorVisitReportRun): void => {
    const key = `${report.id}:${report.attempt}:${report.status}`;
    if (announcedTerminalsRef.current.has(key)) return;
    announcedTerminalsRef.current.add(key);
    setTerminalAnnouncement(announcement);
  }, []);

  const handleAutomaticError = useCallback((): void => {
    setPollingError('Could not refresh one or more report statuses. Use Refresh to try again.');
  }, []);

  return (
    <>
      <PageHeader title="Door Visit Report Runs" backLink={{ label: 'Back to Door Visits', to: '/door-visits' }} />
      <PageControlsBar label="Report runs">
        <Button
          variant="outlined"
          startIcon={reportRuns.refreshing ? <CircularProgress size={16} aria-hidden /> : <RefreshIcon />}
          onClick={reportRuns.refresh}
          disabled={reportRuns.refreshing}
          aria-busy={reportRuns.refreshing}
        >
          Refresh
        </Button>
      </PageControlsBar>
      {pollingError !== null && <Alert severity="warning" sx={{ mb: 2 }}>{pollingError}</Alert>}
      {reportRuns.refreshError !== null && <Alert severity="warning" sx={{ mb: 2 }}>{reportRuns.refreshError}</Alert>}
      {reportRuns.loading && <LoadingState message="Loading report runs…" />}
      {!reportRuns.loading && reportRuns.error !== null && <ErrorState message={reportRuns.error} onRetry={reportRuns.refresh} />}
      {!reportRuns.loading && reportRuns.error === null && reportRuns.items.length === 0 && (
        <EmptyState title="No Door Visit report runs yet" hint="Submitted exports will appear here." />
      )}
      {!reportRuns.loading && reportRuns.error === null && reportRuns.items.length > 0 && (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table aria-label="Door Visit report runs" size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Filter summary</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress / rows</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportRuns.items.map(report => (
                  <ReportRunRow
                    key={report.id}
                    report={report}
                    api={api}
                    recoveryKey={pollingRecoveryKey}
                    requestIdGenerator={requestIdGenerator}
                    onReportChange={reportRuns.updateReport}
                    onReportDeleted={reportRuns.removeReport}
                    onAutomaticError={handleAutomaticError}
                    onTerminal={handleTerminal}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <DoorVisitTableFooter
            total={reportRuns.total}
            page={reportRuns.page}
            rowsPerPage={reportRuns.rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={reportRuns.setPage}
            onRowsPerPageChange={reportRuns.setRowsPerPage}
          />
        </Paper>
      )}
      <Box
        component="span"
        aria-live="polite"
        aria-atomic="true"
        sx={{ position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
      >
        {terminalAnnouncement}
      </Box>
    </>
  );
};
